"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";
import BlackPill from "@/components/getting-started/BlackPill";

import { PageMentoringSchema } from "@/sanity/pages/PageMentoringTemplate";

import logoFullImg from "@/public/lp2/global/logo-full.svg";
import gridGBimg from "@/public/lp2/green-banner/grid-bg.svg";
import blurBGimg from "@/public/lp2/green-banner/blur-bg.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function LastSection({ data }: { data: PageMentoringSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const contentRef = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const lastTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      lastTL
        .from(containerRef.current, {
          y: "2rem",
          delay: 0.5,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(contentRef.current.children, {
          y: "2rem",
          delay: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full">
      <div className="container-lp mx-auto lg:p-[20rem] pt-[16rem] pb-[8rem] !px-[2rem] lg:!px-[4rem] max-w-[188rem]">
        <div className="green-card-section !h-full rounded-[3rem] flex flex-col items-center justify-center w-full py-[10rem] lg:py-[15rem] px-[2rem] lg:px-[13rem] relative">
          {/* Backgrounds */}

          <Image
            className="absolute z-[-1] inset-0 w-full h-full pointer-events-none object-cover"
            alt="Grid background"
            src={gridGBimg}
          />
          <Image
            className="green-card-section-blur-img absolute z-[-2] inset-0 w-full h-full pointer-events-none"
            alt="Blur background"
            src={blurBGimg}
          />
          <div className="green-card-section-blur absolute z-[-2] mx-auto w-[80%] h-[65%] pointer-events-none rounded-[23.6875rem]"></div>
          <div className="absolute inset-0 z-[-2] pointer-events-none rounded-[4.4rem] overflow-hidden">
            <HLSVideoPlayer
              controls={false}
              playsInline={true}
              autoPlay={true}
              muted={true}
              loop={true}
              className="!w-full !h-full object-cover pointer-events-none"
              source={{
                embed: false,
                muxVideo: {
                  assetId: "",
                  filename: "video-bg",
                  status: "ready",
                  playbackId: "T02VT02sMc9eTvJcCyP00FizzEcb2eSRE1kTJwO1r9y502g",
                },
                embedUrl: "",
                thumbnail: {
                  src: "",
                  width: 0,
                  height: 0,
                  lqip: "",
                  name: "",
                },
              }}
            />
          </div>

          {/* End backgrounds */}

          <div
            ref={contentRef}
            className="w-full flex flex-col items-center justify-center relative"
          >
            <Image
              className="h-[4.125rem] lg:h-[5.625rem] mb-[5rem]"
              alt="Logo"
              src={logoFullImg}
            />

            {data?.lastTexts && (
              <PortableText
                value={data?.lastTexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill
                        text={children as string}
                        className="text-[#ffffffd9] bg-black/20 border-[#ffffff38] uppercase backdrop-blur-[1rem]"
                      />
                    ),
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.16rem] lg:tracking-[-0.28rem] leading-[115%] text-center mt-[3rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="sans text-[2.25rem] text-center font-[400] leading-[140%] text-white/80 max-w-[116rem] mx-auto my-[2rem]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}

            <div className="mt-[2rem]">
              {data?.lastCTA && <CTAButton data={data?.lastCTA} />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
